import { useCallback, useEffect, useMemo, useState } from "react";
import { Bot } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubUserAvatar } from "@/components/HubUserAvatar";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Label, TextArea } from "@/components/ui/Input";
import { DeferredCommitStatusGlyph } from "@/components/mrp/DeferredCommitStatusGlyph";
import { CardTitleWithHelp, HoverHintTrigger, PageTitleWithHelp } from "@/components/ui/title-help";
import { downloadPdfBase64 } from "@/lib/miscPrintingApi";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { EM_DASH } from "@/lib/utils";

import { qualityEventLabel, qualityNcrLabel } from "./qualityEventNcrApi";
import {
  fetchQualityRca,
  fetchQualityRcaCustomerPdf,
  linkQualityRca,
  qualityRcaQueryKey,
  saveQualityRca,
  type QualityRcaDetail,
  type QualityRcaLinkInput,
} from "./qualityRcaApi";
import { QualityRmaLinkIdModal } from "./QualityRmaLinkIdModal";
import { presentRcaForm, type RcaFlowStep, type RcaFormState } from "./rcaForm";
import type { QualityRmaDetailResponse } from "./rmaApi";
import {
  QualityRcaWorksheet,
  type RcaDeviceAutomaticLog,
  type RcaLinkedDocuments,
  type RcaLinkedIncident,
} from "./QualityRcaWorksheet";
import type { RcaWorksheetDevice } from "./QualityRcaDevicesCard";

const HELP_TEXT =
  "Root cause analysis. Opened from an RMA, NCR, event, or case once the failure needs analysis. Device-level work lives on each NCR; this worksheet holds the cross-device analysis and the customer report.";

export function QualityRcaDetailPage() {
  const params = useParams<{ id: string }>();
  const rcaId = Number(params.id);
  const queryClient = useQueryClient();
  const writesAllowed = useHubModuleWrite("quality");
  const detailQuery = useQuery({
    queryKey: qualityRcaQueryKey(rcaId),
    queryFn: () => fetchQualityRca(rcaId),
    enabled: Number.isFinite(rcaId) && rcaId > 0,
  });
  const [saveActivity, setSaveActivity] = useState<{ pending: boolean; error: string | null }>({
    pending: false,
    error: null,
  });
  const onSaveActivity = useCallback((next: { pending: boolean; error: string | null }) => {
    setSaveActivity(next);
  }, []);
  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: qualityRcaQueryKey(rcaId) });
  }, [queryClient, rcaId]);

  const statusMutation = useMutation({
    mutationFn: (status: "open" | "closed") => saveQualityRca(rcaId, { status }),
    onSuccess: (updated) => queryClient.setQueryData(qualityRcaQueryKey(rcaId), updated),
  });

  if (!Number.isFinite(rcaId) || rcaId < 1) {
    return <p className="text-sm text-red-700">Invalid RCA id.</p>;
  }
  if (detailQuery.isLoading) {
    return <p className="text-sm text-slate-600">Loading RCA.</p>;
  }
  const detail = detailQuery.data;
  if (detailQuery.isError || detail == null) {
    return <p className="text-sm text-red-700">RCA was not found.</p>;
  }
  const rca = detail.rca;
  const personLabel = rca.openedByDisplay?.trim() || "";
  const openedByPerson = !rca.openedAutomatically && personLabel !== "";
  const saveHint = saveActivity.error ?? "Saved when you leave a field";

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <PageTitleWithHelp hoverHint={HELP_TEXT} breadcrumbLabel={`RCA ${rca.id}`}>
            RCA {rca.id}
          </PageTitleWithHelp>
          <Badge tone={rca.status === "closed" ? "neutral" : "info"}>{rca.status === "closed" ? "Closed" : "Open"}</Badge>
          {rca.openedAutomatically ? (
            <MrRobotoBadge />
          ) : openedByPerson ? (
            <PersonBadge label={personLabel} avatarUrl={rca.openedByAvatarUrl} />
          ) : (
            <span className="text-sm text-slate-600">Opened {rca.openedAt.slice(0, 10)}</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <HoverHintTrigger
            align="end"
            trigger={
              <span className="inline-flex items-center gap-1.5 text-sm text-slate-600">
                <DeferredCommitStatusGlyph
                  pending={saveActivity.pending}
                  isDirty={false}
                  disabled={false}
                  commitError={saveActivity.error}
                  showSavedWhenIdle
                  savedHint={saveHint}
                />
                {saveActivity.error ? "Not saved" : saveActivity.pending ? "Saving" : "Saved"}
              </span>
            }
          >
            {saveHint}
          </HoverHintTrigger>
          {writesAllowed ? (
            <Button
              type="button"
              variant="secondary"
              disabled={statusMutation.isPending}
              onClick={() => statusMutation.mutate(rca.status === "closed" ? "open" : "closed")}
            >
              {rca.status === "closed" ? "Reopen RCA" : "Close RCA"}
            </Button>
          ) : null}
          <CustomerPdfButton rcaId={rca.id} />
        </div>
      </div>
      <RcaLinksCard detail={detail} writesAllowed={writesAllowed} onChanged={refresh} />
      <RcaFormHost
        key={rca.id}
        detail={detail}
        writesAllowed={writesAllowed}
        onSaveActivity={onSaveActivity}
        onNcrChanged={refresh}
      />
    </div>
  );
}

function RcaLinksCard({
  detail,
  writesAllowed,
  onChanged,
}: {
  detail: QualityRcaDetail;
  writesAllowed: boolean;
  onChanged: () => Promise<void>;
}) {
  const queryClient = useQueryClient();
  const rca = detail.rca;
  const [problemStatement, setProblemStatement] = useState(rca.problemStatement ?? "");
  const [linkKind, setLinkKind] = useState<"rma" | "ncr" | "case" | "event" | null>(null);
  const [problemError, setProblemError] = useState<string | null>(null);
  useEffect(() => {
    setProblemStatement(rca.problemStatement ?? "");
  }, [rca.problemStatement]);

  const saveProblem = useMutation({
    mutationFn: (value: string) => saveQualityRca(rca.id, { problemStatement: value.trim() === "" ? null : value.trim() }),
    onSuccess: (updated) => {
      setProblemError(null);
      queryClient.setQueryData(qualityRcaQueryKey(rca.id), updated);
    },
    onError: (error: Error) => setProblemError(error.message || "Could not save the problem statement."),
  });

  const link = async (input: QualityRcaLinkInput) => {
    const updated = await linkQualityRca(rca.id, input);
    queryClient.setQueryData(qualityRcaQueryKey(rca.id), updated);
    await onChanged();
  };

  const modal =
    linkKind === "rma"
      ? { title: "Link RMA", idLabel: "RMA id", onLink: (id: number) => link({ rmaId: id }) }
      : linkKind === "ncr"
        ? { title: "Link NCR", idLabel: "NCR number", onLink: (id: number) => link({ qualityIncidentIds: [id] }) }
        : linkKind === "case"
          ? { title: "Link quality case", idLabel: "Quality case id", onLink: (id: number) => link({ qualityCaseId: id }) }
          : linkKind === "event"
            ? { title: "Link quality event", idLabel: "Event number", onLink: (id: number) => link({ qualityEventId: id }) }
            : null;

  return (
    <Card>
      <CardHeader>
        <CardTitleWithHelp hoverHint="What this RCA is about and which records feed it. Several RMAs and NCRs can share one RCA.">
          Problem and links
        </CardTitleWithHelp>
      </CardHeader>
      <CardBody className="space-y-4">
        <div>
          <Label htmlFor="rca-problem-statement">Problem statement</Label>
          <TextArea
            id="rca-problem-statement"
            value={problemStatement}
            rows={3}
            readOnly={!writesAllowed}
            onChange={(event) => setProblemStatement(event.target.value)}
            onBlur={(event) => {
              if (!writesAllowed) return;
              if ((event.target.value.trim() || null) === (rca.problemStatement?.trim() || null)) return;
              saveProblem.mutate(event.target.value);
            }}
            className="font-sans"
          />
          {problemError ? <p className="mt-1 text-sm text-red-700">{problemError}</p> : null}
        </div>
        <dl className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">RMAs</dt>
            <dd className="space-y-0.5">
              {detail.rmas.length === 0 ? (
                <span className="text-slate-500">{EM_DASH}</span>
              ) : (
                detail.rmas.map((rma) => (
                  <div key={rma.rma.id}>
                    <Link className="text-sky-700 hover:underline" to={`/quality/rmas/${rma.rma.id}`}>
                      {rma.rma.rmaNumber}
                    </Link>
                    {rma.rma.id === rca.rmaId ? <span className="ml-1 text-xs text-slate-500">(owner)</span> : null}
                  </div>
                ))
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">NCRs</dt>
            <dd className="space-y-0.5">
              {detail.ncrs.length === 0 ? (
                <span className="text-slate-500">{EM_DASH}</span>
              ) : (
                detail.ncrs.map((ncr) => (
                  <div key={ncr.id}>
                    <Link className="text-sky-700 hover:underline" to={`/quality/ncrs/${ncr.id}`}>
                      {qualityNcrLabel(ncr.id)}
                    </Link>
                    {ncr.deviceLabelSerial ? <span className="ml-1 font-mono text-xs text-slate-600">{ncr.deviceLabelSerial}</span> : null}
                  </div>
                ))
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Quality case</dt>
            <dd>
              {detail.qualityCase == null ? (
                <span className="text-slate-500">{EM_DASH}</span>
              ) : (
                <Link className="text-sky-700 hover:underline" to={`/quality/cases/${detail.qualityCase.id}`}>
                  Case {detail.qualityCase.id}: {detail.qualityCase.title}
                </Link>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Quality event</dt>
            <dd>
              {detail.qualityEvent == null ? (
                <span className="text-slate-500">{EM_DASH}</span>
              ) : (
                <Link className="text-sky-700 hover:underline" to={`/quality/events/${detail.qualityEvent.id}`}>
                  {qualityEventLabel(detail.qualityEvent.id)}
                  {detail.qualityEvent.summary ? `: ${detail.qualityEvent.summary}` : ""}
                </Link>
              )}
            </dd>
          </div>
        </dl>
        {writesAllowed ? (
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="secondary" onClick={() => setLinkKind("rma")}>
              Link RMA
            </Button>
            <Button type="button" size="sm" variant="secondary" onClick={() => setLinkKind("ncr")}>
              Link NCR
            </Button>
            {detail.qualityCase == null ? (
              <Button type="button" size="sm" variant="secondary" onClick={() => setLinkKind("case")}>
                Link quality case
              </Button>
            ) : null}
            {detail.qualityEvent == null ? (
              <Button type="button" size="sm" variant="secondary" onClick={() => setLinkKind("event")}>
                Link quality event
              </Button>
            ) : null}
          </div>
        ) : null}
        {modal != null ? (
          <QualityRmaLinkIdModal
            open
            title={modal.title}
            idLabel={modal.idLabel}
            submitLabel="Link"
            onClose={() => setLinkKind(null)}
            onLink={modal.onLink}
          />
        ) : null}
      </CardBody>
    </Card>
  );
}

function RcaFormHost({
  detail,
  writesAllowed,
  onSaveActivity,
  onNcrChanged,
}: {
  detail: QualityRcaDetail;
  writesAllowed: boolean;
  onSaveActivity: (next: { pending: boolean; error: string | null }) => void;
  onNcrChanged: () => Promise<void>;
}) {
  const queryClient = useQueryClient();
  const rca = detail.rca;
  const presentInput = useMemo(() => buildPresentInput(detail), [detail]);
  const [form, setForm] = useState<RcaFormState>(() => presentRcaForm(rca.form, presentInput));
  const [saveError, setSaveError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (next: RcaFormState) => saveQualityRca(rca.id, { form: next }),
    onSuccess: (updated) => {
      setSaveError(null);
      queryClient.setQueryData(qualityRcaQueryKey(rca.id), updated);
    },
    onError: (error: Error) => {
      setSaveError(error.message || "Could not save the RCA form.");
    },
  });
  useEffect(() => {
    onSaveActivity({ pending: save.isPending, error: saveError });
  }, [onSaveActivity, save.isPending, saveError]);

  return (
    <QualityRcaWorksheet
      form={form}
      documents={linkedDocuments(detail)}
      devices={worksheetDevices(detail)}
      deviceLogs={deviceAutomaticLogs(detail.rmas)}
      pcbaLocationBySerial={pcbaLocationBySerial(detail.rmas)}
      ncrDeviceWork={detail.ncrDeviceWork}
      rcaId={rca.id}
      writesAllowed={writesAllowed}
      onChange={setForm}
      onSave={(next) => {
        setForm(next);
        if (writesAllowed) save.mutate(next);
      }}
      onNcrChanged={onNcrChanged}
    />
  );
}

function PersonBadge({ label, avatarUrl }: { label: string; avatarUrl: string | null }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-slate-200 bg-white py-0.5 pl-0.5 pr-2.5 text-xs font-medium text-slate-800">
      <HubUserAvatar avatarUrl={avatarUrl} label={label} size="sm" className="h-5 w-5 text-[10px]" />
      <span className="truncate">{label}</span>
    </span>
  );
}

function MrRobotoBadge() {
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-slate-200 bg-white py-0.5 pl-0.5 pr-2.5 text-xs font-medium text-slate-800">
      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-200 bg-slate-100 text-slate-600">
        <Bot className="h-3 w-3" aria-hidden />
      </span>
      Mr. Roboto
    </span>
  );
}

function CustomerPdfButton({ rcaId }: { rcaId: number }) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const pdf = useMutation({
    mutationFn: () => fetchQualityRcaCustomerPdf(rcaId),
    onSuccess: (payload) => {
      setErrorMessage(null);
      downloadPdfBase64(payload.pdfBase64, payload.filename);
    },
    onError: (error: Error) => {
      setErrorMessage(error.message || "Could not generate the customer PDF.");
    },
  });
  return (
    <>
      <Button type="button" variant="secondary" disabled={pdf.isPending} onClick={() => pdf.mutate()}>
        {pdf.isPending ? "Downloading" : "Download customer report"}
      </Button>
      {errorMessage ? <span className="text-red-700">{errorMessage}</span> : null}
    </>
  );
}

function serialKey(value: string | null | undefined): string {
  return (value ?? "").trim().toUpperCase();
}

/** One row per device serial across linked RMAs and NCRs, each with its NCR when one exists. */
function worksheetDevices(detail: QualityRcaDetail): RcaWorksheetDevice[] {
  const byKey = new Map<string, RcaWorksheetDevice>();
  for (const rma of detail.rmas) {
    for (const device of rma.devices) {
      const key = serialKey(device.deviceLabelSerial);
      if (key === "") continue;
      const existing = byKey.get(key);
      if (existing == null) {
        byKey.set(key, { serial: device.deviceLabelSerial.trim(), qualityIncidentId: device.qualityIncidentId });
      } else if (existing.qualityIncidentId == null && device.qualityIncidentId != null) {
        existing.qualityIncidentId = device.qualityIncidentId;
      }
    }
    for (const row of rma.intake) {
      const key = serialKey(row.unitSerial);
      if (key === "" || byKey.has(key)) continue;
      byKey.set(key, { serial: (row.unitSerial ?? "").trim(), qualityIncidentId: null });
    }
  }
  for (const ncr of detail.ncrs) {
    const serial = ncr.deviceLabelSerial?.trim() || ncr.pcbaSerial?.trim() || ncr.clampSerial?.trim() || "";
    const key = serialKey(serial);
    if (key === "") continue;
    const existing = byKey.get(key);
    if (existing == null) {
      byKey.set(key, { serial, qualityIncidentId: ncr.id });
    } else {
      // An explicitly linked NCR wins over the RMA device's latest incident.
      existing.qualityIncidentId = ncr.id;
    }
  }
  return [...byKey.values()];
}

function deviceAutomaticLogs(rmas: QualityRmaDetailResponse[]): Record<string, RcaDeviceAutomaticLog> {
  const logs: Record<string, RcaDeviceAutomaticLog> = {};
  for (const rma of rmas) {
    for (const row of rma.intake) {
      const serial = row.unitSerial?.trim() ?? "";
      if (serial === "") continue;
      logs[serial.toUpperCase()] = {
        receivedAt: row.receivedAt,
        loggedAt: row.createdAt.slice(0, 10) || row.receivedAt,
        loggedBy: row.loggedBy,
      };
    }
  }
  return logs;
}

function pcbaLocationBySerial(rmas: QualityRmaDetailResponse[]): Record<string, number> {
  const locations: Record<string, number> = {};
  for (const rma of rmas) {
    for (const row of rma.intake) {
      const serial = row.unitSerial?.trim().toUpperCase() ?? "";
      if (serial !== "" && row.pcbaLocationId != null && row.pcbaLocationId > 0) {
        locations[serial] = row.pcbaLocationId;
      }
    }
    for (const device of rma.devices) {
      const serial = device.deviceLabelSerial.trim().toUpperCase();
      if (serial !== "" && device.pcbaLocationId != null && device.pcbaLocationId > 0) {
        locations[serial] = device.pcbaLocationId;
      }
    }
  }
  return locations;
}

function linkedDocuments(detail: QualityRcaDetail): RcaLinkedDocuments {
  const rmas = detail.rmas.map((rma) => {
    const received =
      rma.intake.map((row) => row.receivedAt).find((value) => value != null && value.trim() !== "") ?? null;
    return {
      id: rma.rma.id,
      rmaNumber: rma.rma.rmaNumber,
      status: rma.rma.status,
      customerName: rma.rma.customerName,
      contactName: rma.rma.contactName,
      contactPhone: rma.rma.contactPhone,
      dateReceived: received,
      quantityAndModels: rma.rma.quantityAndModels,
      complaint: rma.rma.complaint,
    };
  });
  return {
    investigations: detail.investigations,
    rmas,
    incidents: linkedIncidents(detail),
  };
}

function linkedIncidents(detail: QualityRcaDetail): RcaLinkedIncident[] {
  const byId = new Map<number, RcaLinkedIncident>();
  for (const ncr of detail.ncrs) {
    const serials = [ncr.deviceLabelSerial, ncr.clampSerial, ncr.pcbaSerial]
      .map((value) => value?.trim() ?? "")
      .filter((value) => value !== "");
    const skus = [ncr.finishedSku, ncr.productSku].map((value) => value?.trim() ?? "").filter((value) => value !== "");
    byId.set(ncr.id, {
      id: ncr.id,
      serials,
      skus,
      open: ncr.resolutionDate == null,
      repairScrap: ncr.repairScrap,
    });
  }
  for (const rma of detail.rmas) {
    for (const device of rma.devices) {
      if (device.qualityIncidentId == null) continue;
      const existing = byId.get(device.qualityIncidentId);
      const serial = device.deviceLabelSerial.trim();
      const sku = device.finishedSku?.trim() ?? "";
      if (existing == null) {
        byId.set(device.qualityIncidentId, {
          id: device.qualityIncidentId,
          serials: serial === "" ? [] : [serial],
          skus: sku === "" ? [] : [sku],
          open: device.qualityIncidentOpen,
          repairScrap: device.qualityIncidentRepairScrap,
        });
        continue;
      }
      if (serial !== "" && !existing.serials.includes(serial)) existing.serials.push(serial);
      if (sku !== "" && !existing.skus.includes(sku)) existing.skus.push(sku);
      existing.open = device.qualityIncidentOpen;
      existing.repairScrap = device.qualityIncidentRepairScrap;
    }
  }
  return [...byId.values()];
}

function buildPresentInput(detail: QualityRcaDetail) {
  const primary = detail.rmas[0] ?? null;
  const intake = primary?.intake[0] ?? null;
  const device = primary?.devices[0] ?? null;
  const firstNcr = detail.ncrs[0] ?? null;
  const serials = worksheetDevices(detail).map((row) => row.serial);
  const voltages = [intake?.aaVoltage ? `AA ${intake.aaVoltage}` : "", intake?.cVoltage ? `C ${intake.cVoltage}` : ""]
    .filter((value) => value !== "")
    .join(", ");
  return {
    serials,
    report: {
      customer: primary?.rma.customerName?.trim() || "",
      dateReceived: intake?.receivedAt?.slice(0, 10) || "",
      customerContact: primary?.rma.contactName?.trim() || "",
      phone: primary?.rma.contactPhone?.trim() || "",
      model: device?.finishedSku?.trim() || intake?.unitModel?.trim() || firstNcr?.finishedSku?.trim() || "",
      eventDescription: primary?.rma.complaint?.trim() || detail.rca.problemStatement?.trim() || "",
    },
    firstDevice: {
      initialVoltages: voltages,
      visualInspection: intake?.visualInspection?.trim() || "",
    },
    statusSeeds: primary == null ? {} : rcaStatusSeeds(primary),
  };
}

function rcaStatusSeeds(detail: QualityRmaDetailResponse): Partial<Record<RcaFlowStep, string>> {
  const seeds: Partial<Record<RcaFlowStep, string>> = {};
  if (detail.rma.createdAt.trim() !== "") seeds["Waiting for intake"] = detail.rma.createdAt;
  const received = detail.intake
    .map((row) => row.receivedAt?.trim() ?? "")
    .filter((value) => value !== "")
    .sort()[0];
  if (received) seeds.Received = received;
  const deviceSerials = detail.devices
    .map((row) => row.deviceLabelSerial.trim().toUpperCase())
    .filter((value) => value !== "");
  const intakeSerials = new Set(
    detail.intake.map((row) => (row.unitSerial ?? "").trim().toUpperCase()).filter((value) => value !== ""),
  );
  const intakeComplete = deviceSerials.length > 0 && deviceSerials.every((serial) => intakeSerials.has(serial));
  if (intakeComplete) {
    const latest = detail.intake
      .map((row) => row.createdAt.trim())
      .filter((value) => value !== "")
      .sort()
      .at(-1);
    if (latest) seeds["Intake complete"] = latest;
  }
  return seeds;
}
